/**
 * AvatarMenu — Topbar user avatar with dropdown menu
 *
 * Provides quick access to:
 *   • Account Settings (navigates to profile page)
 *   • Theme quick-switch
 *   • Language selector
 *   • Sign Out
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useTheme } from '../../theme';
import { useAccountSettings } from '../AccountSettings/AccountSettingsContext';
import {
  SVG_TEXTURE_IDS, HIRES_TEXTURE_IDS,
  HIRES_TEXTURES, isHiresDownloaded, probeHiresTexture,
  texturesForTheme,
  parseTextureAllowlist,
} from '../../theme/backgroundTexture';
import { getTextureAllowlistRaw } from '../../theme/themeOverrides';
import type { TextureId, HiresTextureId, BaseTheme } from '../../theme/backgroundTexture';
import { useAuth } from '../../auth/AuthContext';
import { useUserThemeApi } from '../../api/userTheme';
import { useEnabledLanguages } from '../../i18n/useEnabledLanguages';
import { clearNumberFormatCache } from '../../utils/numberFormat';
import i18n from '../../i18n/config';
import styles from './AvatarMenu.module.css';

const LANG_FLAGS: Record<string, string> = {
  en: '\uD83C\uDDFA\uD83C\uDDF8', ar: '\uD83C\uDDF8\uD83C\uDDE6',
  fr: '\uD83C\uDDEB\uD83C\uDDF7', de: '\uD83C\uDDE9\uD83C\uDDEA',
  uk: '\uD83C\uDDFA\uD83C\uDDE6', sv: '\uD83C\uDDF8\uD83C\uDDEA',
  ru: '\uD83C\uDDF7\uD83C\uDDFA', es: '\uD83C\uDDEA\uD83C\uDDF8',
};

interface AvatarMenuProps {
  displayName: string;
  profileImg: string;
  initials: string;
  isEnabler: boolean;
}

export default function AvatarMenu({
  displayName,
  profileImg,
  initials,
  isEnabler,
}: AvatarMenuProps) {
  const { t } = useAppTranslation('userProfile');
  const { t: tTheme } = useAppTranslation('theme');
  const navigate = useNavigate();
  const { logout, isFakeAuth } = useAuth();
  const { open: openAccountSettings } = useAccountSettings();
  const { themeName, setThemeName, availableThemes, textureId, setTextureId } = useTheme();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // Reset failure state when the URL changes (e.g. user uploads a new photo)
  const effectiveProfileImg = (profileImg && profileImg !== failedUrl) ? profileImg : '';
  const { enabledLanguages, loading: langsLoading } = useEnabledLanguages();

  // If the user's current language was disabled by admin, fall back to English
  useEffect(() => {
    const current = i18n.language || 'en';
    if (!langsLoading && !enabledLanguages.includes(current as any) && current !== 'en') {
      i18n.changeLanguage('en');
      try { localStorage.setItem('l4e_language', 'en'); } catch { /* ignore */ }
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    }
  }, [enabledLanguages, langsLoading]);
  // Derive base theme for texture compatibility filtering
  const currentBaseTheme: BaseTheme = (() => {
    const th = availableThemes.find((t) => t.name === themeName);
    if (th?.isCustom && th.baseTheme) return th.baseTheme;
    return themeName === 'light' ? 'light' : 'dark';
  })();
  // DEV-1478 — pass the theme NAME as well as the base. Compatibility is now
  // per built-in theme (Hex Glow Black → Ninja, Blue → Dark), so a base-only
  // call would still offer both glows to every dark theme.
  // DEV-1478 — and an admin may have overridden the pairing for this theme in
  // the Theme Designer; that allowlist wins over the code defaults.
  const compatibleTextures = texturesForTheme(
    currentBaseTheme,
    themeName,
    parseTextureAllowlist(getTextureAllowlistRaw(themeName)),
  );
  const { updateTheme } = useUserThemeApi();

  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [bgOpen, setBgOpen] = useState(false);
  const [hiresProbing, setHiresProbing] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setLangOpen(false);
        setBgOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setLangOpen(false); setBgOpen(false); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleThemeChange = useCallback((name: string) => {
    setThemeName(name);
    updateTheme(name).catch(() => { /* best-effort persist */ });
  }, [setThemeName]);

  const handleTextureChange = useCallback(async (id: TextureId) => {
    // For hi-res textures, probe availability first
    if (HIRES_TEXTURE_IDS.includes(id as HiresTextureId)) {
      if (!isHiresDownloaded(id as HiresTextureId)) {
        setHiresProbing(id);
        const available = await probeHiresTexture(id as HiresTextureId);
        setHiresProbing(null);
        if (!available) {
          // File not yet in public/textures/ — user needs to download it
          const info = HIRES_TEXTURES[id as HiresTextureId];
          window.open(info.downloadUrl, '_blank');
          return;
        }
      }
    }
    setTextureId(id);
  }, [setTextureId]);

  const handleLanguageChange = useCallback((code: string) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('l4e_language', code); } catch { /* ignore */ }
    clearNumberFormatCache();
    const isRtl = ['ar', 'he', 'fa', 'ur'].includes(code);
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    setLangOpen(false);
  }, []);

  const handleSignOut = useCallback(async () => {
    setOpen(false);
    await logout();
    navigate('/welcome');
  }, [logout, navigate]);

  const themeIcon = (th: { name: string; isCustom: boolean; baseTheme?: string; icon?: string }) => {
    if (th.icon) return th.icon;
    const base = th.isCustom ? th.baseTheme : th.name;
    return base === 'light' ? '\u2600\uFE0F' : '\uD83C\uDF19';
  };

  const themeLabel = (th: { name: string; isCustom: boolean; displayName?: string }) => {
    if (th.displayName) return th.displayName;
    if (!th.isCustom) return tTheme(th.name);
    return th.name;
  };

  const currentLang = i18n.language || 'en';

  return (
    <div className={styles.wrapper} ref={menuRef}>
      {/* Avatar trigger button */}
      <button
        type="button"
        className={styles.avatarButton}
        aria-label={displayName || t('avatarMenu.myProfile')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => { setOpen((v) => !v); setLangOpen(false); }}
        style={{
          borderColor: isEnabler ? 'var(--pillar-enablers)' : 'var(--border-subtle)',
        }}
      >
        {effectiveProfileImg ? (
          <img
            src={effectiveProfileImg}
            alt={displayName || 'Profile'}
            className={styles.avatarImg}
            onError={() => setFailedUrl(profileImg)}
          />
        ) : (
          <span className={styles.avatarInitials}>{initials}</span>
        )}
      </button>

      {/* Dropdown menu */}
      {open && (
        <div className={styles.menu} role="menu" aria-label={t('avatarMenu.myProfile')}>
          {/* User identity header — clickable to view profile */}
          <button
            type="button"
            role="menuitem"
            className={styles.header}
            onClick={() => { setOpen(false); navigate('/profile'); }}
          >
            <div className={styles.headerAvatar}>
              {effectiveProfileImg ? (
                <img src={effectiveProfileImg} alt="" className={styles.headerAvatarImg} onError={() => setFailedUrl(profileImg)} />
              ) : (
                <span className={styles.headerInitials}>{initials}</span>
              )}
            </div>
            <div className={styles.headerInfo}>
              <div className={styles.headerName}>{displayName}</div>
              <div className={styles.headerHint}>{t('avatarMenu.viewProfile')}</div>
            </div>
            <svg className={styles.headerChevron} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div className={styles.divider} />

          {/* Account Settings */}
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => { setOpen(false); openAccountSettings(); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span>{t('avatarMenu.accountSettings')}</span>
          </button>

          <div className={styles.divider} />

          {/* Theme quick-switch */}
          <div className={styles.sectionLabel}>{t('avatarMenu.theme')}</div>
          <div className={styles.themeList}>
            {availableThemes.map((th) => (
              <button
                key={th.name}
                type="button"
                role="menuitemradio"
                aria-checked={th.name === themeName}
                className={`${styles.themeOption} ${th.name === themeName ? styles.themeActive : ''}`}
                onClick={() => handleThemeChange(th.name)}
              >
                <span className={styles.themeIcon}>{themeIcon(th)}</span>
                <span className={styles.themeLabel}>{themeLabel(th)}</span>
                {th.name === themeName && (
                  <svg className={styles.checkmark} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          <div className={styles.divider} />

          {/* Background texture sub-menu */}
          <div className={styles.sectionLabel}>{tTheme('background.label')}</div>
          <button
            type="button"
            className={styles.menuItem}
            onClick={() => setBgOpen((v) => !v)}
            aria-expanded={bgOpen}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 3v18" />
            </svg>
            <span>{tTheme(`background.${textureId}`)}</span>
            <svg className={`${styles.chevron} ${bgOpen ? styles.chevronOpen : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {bgOpen && (
            <div className={styles.langList}>
              {compatibleTextures.map((tid) => {
                const isHires = HIRES_TEXTURE_IDS.includes(tid as HiresTextureId);
                const isProbing = hiresProbing === tid;
                const downloaded = isHires && isHiresDownloaded(tid as HiresTextureId);
                return (
                  <button
                    key={tid}
                    type="button"
                    role="menuitemradio"
                    aria-checked={tid === textureId}
                    className={`${styles.langOption} ${tid === textureId ? styles.langActive : ''}`}
                    onClick={() => handleTextureChange(tid)}
                    disabled={isProbing}
                  >
                    <span className={styles.textureIcon}>
                      {tid === 'solid' ? '\u25A0' : SVG_TEXTURE_IDS.includes(tid as any) ? '\u25E8' : '\uD83D\uDDBC'}
                    </span>
                    <span style={{ flex: 1 }}>{tTheme(`background.${tid}`)}</span>
                    {isHires && !downloaded && !isProbing && (
                      <span className={styles.downloadHint}>{tTheme('background.downloadRequired')}</span>
                    )}
                    {isProbing && (
                      <span className={styles.downloadHint}>{tTheme('background.downloading')}</span>
                    )}
                    {tid === textureId && (
                      <svg className={styles.checkmark} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Language sub-menu — hidden when only English is enabled */}
          {enabledLanguages.length > 1 && (
            <>
              <div className={styles.divider} />

              <div className={styles.sectionLabel}>{t('avatarMenu.language')}</div>
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => setLangOpen((v) => !v)}
                aria-expanded={langOpen}
              >
                <span className={styles.langFlag}>{LANG_FLAGS[currentLang] || LANG_FLAGS.en}</span>
                <span>{tTheme(`lang.${currentLang}`)}</span>
                <svg className={`${styles.chevron} ${langOpen ? styles.chevronOpen : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {langOpen && (
                <div className={styles.langList}>
                  {Object.entries(LANG_FLAGS).filter(([code]) => enabledLanguages.includes(code as any)).map(([code, flag]) => (
                    <button
                      key={code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={code === currentLang}
                      className={`${styles.langOption} ${code === currentLang ? styles.langActive : ''}`}
                      onClick={() => handleLanguageChange(code)}
                    >
                      <span className={styles.langFlag}>{flag}</span>
                      <span>{tTheme(`lang.${code}`)}</span>
                      {code === currentLang && (
                        <svg className={styles.checkmark} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <div className={styles.divider} />

          {/* Sign Out */}
          {!isFakeAuth && (
            <button
              type="button"
              role="menuitem"
              className={`${styles.menuItem} ${styles.signOut}`}
              onClick={handleSignOut}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>{t('avatarMenu.signOut')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
